import { useCallback, useState } from "react";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { FloatLabel } from "primereact/floatlabel";
import { InputNumber } from "primereact/inputnumber";
import PageTitle from "../common/PageTitle";
import TabHeader from "../common/TabHeader";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";

export default function CalendarSettings() {
    const { requestAPI, showToast, setApplicationBlocker } = useAppContext();
    const [year, setYear] = useState(new Date().getFullYear());
    const [syncing, setSyncing] = useState(false);

    const syncYear = useCallback(() => {
        if (syncing) return;
        if (!Number.isInteger(year) || year < 2000 || year > 2100) {
            showToast({ severity: "warn", summary: "Year required", detail: "Enter a valid year (2000–2100)", life: 2500 });
            return;
        }

        setSyncing(true);
        setApplicationBlocker({
            title: "Syncing Choghadiya",
            message: `Fetching all ${year} days one-by-one. Keep this open — full year can take 10–20 minutes.`,
        });

        requestAPI({
            requestPath: "calendar/choghadiya/sync",
            requestMethod: "POST",
            requestPostBody: { year },
            onRequestFailure: (err) => {
                showToast({
                    severity: "error",
                    summary: "Sync failed",
                    detail: (typeof err === "string" ? err : err?.error) || "Could not sync choghadiya",
                    life: 3000,
                });
            },
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200 && data.complete) {
                    showToast({
                        severity: "success",
                        summary: "Synced",
                        detail: `Saved full year ${year}: ${data.stored_days || data.days_synced}/${data.days_requested} days`,
                        life: 4000,
                    });
                } else {
                    showToast({
                        severity: "error",
                        summary: "Sync incomplete",
                        detail:
                            data?.error ||
                            `Saved ${data?.stored_days || data?.days_synced || 0}/${data?.days_requested || 365} days. Try Sync again.`,
                        life: 5000,
                    });
                }
            },
            onRequestEnd: () => {
                setSyncing(false);
                setApplicationBlocker(false);
            },
        });
    }, [requestAPI, setApplicationBlocker, showToast, syncing, year]);

    return (
        <div className="flex flex-column h-full overflow-hidden">
            <PageTitle title="Calendar Settings" />
            <TabHeader
                className="px-3 pt-3"
                title="Choghadiya Sync"
                highlights={["Location is taken from server env", "Sync replaces previous stored year data"]}
            />
            <Divider />

            <div className="flex-1 min-h-0 overflow-scroll px-3 pb-3">
                <div className="border-1 border-gray-200 border-round p-3 flex flex-column gap-3">
                    <div className={`${TEXT_NORMAL} font-semibold`}>Sync year</div>
                    <div className={`${TEXT_SMALL} text-color-secondary`}>
                        Select the year to fetch and store. Location and time come from server environment variables.
                    </div>

                    <div className="w-full md:w-14rem">
                        <FloatLabel>
                            <InputNumber
                                inputId="choghadiya-year"
                                value={year}
                                onValueChange={(e) => setYear(e.value)}
                                useGrouping={false}
                                min={2000}
                                max={2100}
                                className="w-full"
                            />
                            <label htmlFor="choghadiya-year">Year</label>
                        </FloatLabel>
                    </div>

                    <div className="flex justify-content-end">
                        <ConfirmationWrapper action={syncYear}>
                            <Button
                                label={syncing ? "Syncing…" : "Sync & Replace"}
                                icon={syncing ? "pi pi-spin pi-spinner" : "pi pi-cloud-download"}
                                severity="warning"
                                disabled={syncing}
                            />
                        </ConfirmationWrapper>
                    </div>
                </div>
            </div>
        </div>
    );
}
